// Shared types and helpers for the Outreach drafts workspace (#3169).

export interface DraftEntry {
  label: string;
  body: string;
}

export interface DraftVersion {
  id: string;
  messages: DraftEntry[];
  generationState: "draft" | "generating" | "error";
  generationError: string | null;
  versionNumber: number;
  origin: string;
  changeSummary?: string | null;
}

export interface Draft {
  id: string;
  kind: string;
  totalVersions: number;
  currentVersion: DraftVersion | null;
}

export interface ContactLogEntry {
  id: string;
  channel: string;
  direction: string;
  status: string;
  createdVia?: string;
  messageContent: string | null;
  responseContent: string | null;
  sentAt: string | null;
  respondedAt: string | null;
  sentBy: { id: string; name: string };
  createdAt: string;
}

export interface WorkspaceCandidate {
  id: string;
  stage: string;
  candidate: {
    id: string;
    referenceNumber?: number;
    fullName: string;
    email: string | null;
    linkedinUrl: string | null;
    currentRoleTitle: string | null;
    currentCompany: string | null;
  };
  emailMessage: Draft | null;
  linkedinMessages: Draft | null;
  linkedinConnectionRequest: Draft | null;
  outreachAttempts: ContactLogEntry[];
}

export type DraftKind = "email" | "linkedin_message" | "linkedin_connection_request";

export interface ChannelSpec {
  kind: DraftKind;
  label: string;
  shortLabel: string;
  pluralLabel: string;
  draftKey: "emailMessage" | "linkedinMessages" | "linkedinConnectionRequest";
  /** Contact-log channel to prefill when the recruiter marks the draft as sent. */
  logChannel: "email" | "linkedin";
}

export const CHANNELS: ChannelSpec[] = [
  {
    kind: "email",
    label: "Email",
    shortLabel: "Email",
    pluralLabel: "emails",
    draftKey: "emailMessage",
    logChannel: "email",
  },
  {
    kind: "linkedin_message",
    label: "LinkedIn messages",
    shortLabel: "LinkedIn",
    pluralLabel: "LinkedIn messages",
    draftKey: "linkedinMessages",
    logChannel: "linkedin",
  },
  {
    kind: "linkedin_connection_request",
    label: "Connection request",
    shortLabel: "Connect",
    pluralLabel: "connection requests",
    draftKey: "linkedinConnectionRequest",
    logChannel: "linkedin",
  },
];

export function channelSpec(kind: string | null | undefined): ChannelSpec {
  return CHANNELS.find((c) => c.kind === kind) ?? CHANNELS[0];
}

export function draftFor(cc: WorkspaceCandidate, spec: ChannelSpec): Draft | null {
  return cc[spec.draftKey];
}

export type DraftState = "none" | "generating" | "draft" | "edited" | "error";

export function draftState(draft: Draft | null | undefined): DraftState {
  const version = draft?.currentVersion;
  if (!version) return "none";
  if (version.generationState === "generating") return "generating";
  if (version.generationState === "error") return "error";
  return version.origin === "user" ? "edited" : "draft";
}

export function draftStateLabel(draft: Draft | null | undefined): string {
  const version = draft?.currentVersion;
  switch (draftState(draft)) {
    case "none":
      return "—";
    case "generating":
      return "Drafting…";
    case "error":
      return "Error";
    case "edited":
      return `Edited v${version?.versionNumber ?? ""}`;
    default:
      return `Draft v${version?.versionNumber ?? ""}`;
  }
}

/** The draft as plain text, ready for the recruiter's own client — and for the
 *  contact log when they mark it as sent. */
export function draftText(kind: DraftKind, entries: DraftEntry[]): string {
  if (kind === "email") {
    const subject = entries.find((e) => e.label === "Subject")?.body ?? "";
    const body = entries.find((e) => e.label === "Body")?.body ?? "";
    return subject ? `Subject: ${subject}\n\n${body}` : body;
  }
  if (kind === "linkedin_connection_request") return entries[0]?.body ?? "";
  return entries.map((e) => `${e.label}:\n${e.body}`).join("\n\n");
}

/** Inverse of draftText for the email kind, mirroring
 *  Outreach::RecoverDraftsFromContactLog.split_subject: a leading
 *  "Subject: …" line becomes the subject, the rest is the body. Message
 *  content without one comes back as body only. */
export function splitSubject(content: string | null | undefined): {
  subject: string;
  body: string;
} {
  const text = content ?? "";
  const match = text.match(/^\s*subject\s*:\s*(.+?)\s*\n+/i);
  if (!match) return { subject: "", body: text.trim() };
  return { subject: match[1].trim(), body: text.slice(match[0].length).trim() };
}

export function latestContact(cc: WorkspaceCandidate): ContactLogEntry | null {
  return (
    [...cc.outreachAttempts].sort((a, b) =>
      (b.sentAt ?? b.createdAt).localeCompare(a.sentAt ?? a.createdAt),
    )[0] ?? null
  );
}

export function contactSummary(entry: ContactLogEntry): string {
  const when = new Date(entry.sentAt ?? entry.createdAt).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
  });
  if (entry.direction === "inbound") return `Reply logged via ${entry.channel} ${when}`;
  return `Logged: ${entry.channel} ${entry.status.replace("_", " ")} ${when}`;
}
